import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '@tes/ui';

interface LinkedSigner {
  signerId: string;
  name: string;
  email: string;
  /** null = no live link to show (e.g. not yet their turn). */
  signingUrl: string | null;
}

/** Copies one signing link; falls back to a prompt where the clipboard is blocked. */
export function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Copy this link:', url);
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={() => void copy()}>
      {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
      {copied ? 'Copied' : 'Copy link'}
    </Button>
  );
}

/**
 * Signing links with a copy button each, under a headline saying what just
 * happened (sent, resent, replaced). `tone` says whether n8n has them to email
 * ('ok') or nobody does ('warn').
 */
export function SigningLinks({
  signers,
  tone,
  children,
}: {
  signers: LinkedSigner[];
  tone: 'ok' | 'warn';
  /** The headline: what happened and who, if anyone, emails these links. */
  children: ReactNode;
}) {
  const withLinks = signers.filter((s): s is LinkedSigner & { signingUrl: string } => !!s.signingUrl);
  return (
    <div
      className={`rounded-lg px-3 py-2 text-sm ${tone === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}
    >
      <div className="font-medium">{children}</div>
      {withLinks.length > 0 && (
        <ul className="mt-2 space-y-1">
          {withLinks.map((s) => (
            <li key={s.signerId} className="flex items-center justify-between gap-2">
              <span className="truncate">
                <span className="font-medium">{s.name}</span>{' '}
                <span className="opacity-70">&lt;{s.email}&gt;</span>
              </span>
              <CopyLinkButton url={s.signingUrl} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
